import React, { useState, useEffect, useRef } from 'react';
import {
  useGetTasksListQuery, useSearchTasksQuery, useGetTaskByIdQuery,
  type TaskFull,
} from '@/redux/internal-services/workspaceApiExtensions';
import { Spinner } from '@/components/UI/Spinner';
import CreatedIdsChip from '@/components/common/CreatedIdsChip';
import { Pagination } from '@/components/UI/Pagination';
import TaskCard from './TaskCard';
import TaskListFilters from './TaskListFilters';
import { useAppSelector } from '@/hooks';
import { WORKSPACE_QUERY_OPTIONS } from '@/components/chat/workspaceQueryOptions';
import { useWorkspaceFilterReport, type OnWorkspaceFilterChange } from '@/hooks/useWorkspaceFilterReport';

// ─── Date helpers (called at interaction/render time, not module load) ─────────
function todayStr() { return new Date().toISOString().split('T')[0]; }
function daysAgoStr(n: number) { return new Date(Date.now() - n * 86400000).toISOString().split('T')[0]; }
function startOfWeekStr() {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().split('T')[0];
}
function endOfWeekStr() {
  const d = new Date();
  d.setDate(d.getDate() + (6 - d.getDay()));
  return d.toISOString().split('T')[0];
}

/** Normalise a due-date bound to an ISO instant for the API.
 *
 *  Two shapes arrive here: 'YYYY-MM-DD' from the date inputs and preset chips, and a full ISO
 *  string from a SAM-published filter. A date names a whole DAY, so the upper bound has to be
 *  that day's last moment — sending plain midnight asked for an empty window and returned nothing
 *  for "tasks due on the 18th". A value that already carries a time is passed through untouched;
 *  the previous code appended 'T23:59:59' unconditionally, which turned a full ISO string into
 *  '…T00:00:00.000ZT23:59:59' and an Invalid Date. */
function dueBound(value: string, endOfDay: boolean): string | undefined {
  if (!value) return undefined;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = new Date(dateOnly ? `${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}` : value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

// ─── Types ─────────────────────────────────────────────────────────────────────
/** Filters SAM's search_tasks can publish that have no chip of their own. They still have to
 *  reach the list query, or the panel contradicts the answer SAM just gave. */
export interface TaskAdvancedFilters {
  exclude_assignee_user_id?: string;
  created_from?: string;
  created_to?: string;
  no_due_date?: boolean;
  /** Explicit "NOT overdue" — sent as overdue=false. */
  not_overdue?: boolean;
  title_starts_with?: string;
}

interface FilterState {
  status: string;
  priority: string;
  taskType: string;
  dueDateFrom: string;
  dueDateTo: string;
  overdue: boolean;
  assigneeUserId: string;
  ownerUserId: string;
  updatedByUserId: string;
  followerUserId: string;
}

interface Preset {
  id: string;
  label: string;
  icon: React.ReactNode;
  build: (userId: string) => Partial<FilterState>;
  activeCheck: (f: FilterState, userId: string) => boolean;
}

// SVG icon components
const IconUser = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);
const IconClock = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconTarget = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1" fill="currentColor"/>
  </svg>
);
const IconFlame = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M12 2c0 6-5 8-5 13a5 5 0 0010 0c0-5-5-7-5-13z"/><path d="M12 12c0 3-2 4-2 6a2 2 0 004 0c0-2-2-3-2-6z"/>
  </svg>
);
const IconAlert = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const PRESETS: Preset[] = [
  {
    id: 'my_tasks',
    label: 'My Tasks',
    icon: <IconUser />,
    build: (userId) => ({ assigneeUserId: userId }),
    activeCheck: (f, userId) => !!userId && f.assigneeUserId === userId,
  },
  {
    id: 'created_by_me',
    label: 'Created by Me',
    icon: <IconUser />,
    build: (userId) => ({ ownerUserId: userId }),
    activeCheck: (f, userId) => !!userId && f.ownerUserId === userId,
  },
  {
    id: 'updated_by_me',
    label: 'Updated by Me',
    icon: <IconUser />,
    build: (userId) => ({ updatedByUserId: userId }),
    activeCheck: (f, userId) => !!userId && f.updatedByUserId === userId,
  },
  {
    id: 'following',
    label: 'Following',
    icon: <IconUser />,
    build: (userId) => ({ followerUserId: userId }),
    activeCheck: (f, userId) => !!userId && f.followerUserId === userId,
  },
  {
    id: 'overdue',
    label: 'Overdue',
    icon: <IconClock />,
    build: () => ({ overdue: true, status: 'open' }),
    activeCheck: (f) => f.overdue && !f.dueDateFrom && !f.dueDateTo && f.status === 'open',
  },
  {
    id: 'overdue_week',
    label: 'Overdue This Week',
    icon: <IconCalendar />,
    build: () => ({ overdue: true, status: 'open', dueDateFrom: startOfWeekStr(), dueDateTo: todayStr() }),
    activeCheck: (f) => f.overdue && f.status === 'open' && f.dueDateFrom === startOfWeekStr() && f.dueDateTo === todayStr(),
  },
  {
    id: 'due_today',
    label: 'Due Today',
    icon: <IconTarget />,
    build: () => ({ dueDateFrom: todayStr(), dueDateTo: todayStr() }),
    activeCheck: (f) => !f.overdue && f.dueDateFrom === todayStr() && f.dueDateTo === todayStr(),
  },
  {
    id: 'due_this_week',
    label: 'Due This Week',
    icon: <IconCalendar />,
    build: () => ({ dueDateFrom: todayStr(), dueDateTo: endOfWeekStr() }),
    activeCheck: (f) => !f.overdue && f.dueDateFrom === todayStr() && f.dueDateTo === endOfWeekStr(),
  },
  {
    id: 'high_priority',
    label: 'High Priority',
    icon: <IconFlame />,
    build: () => ({ priority: 'high' }),
    activeCheck: (f) => f.priority === 'high' && !f.overdue,
  },
  {
    id: 'critical_overdue',
    label: 'Critical Overdue 2d+',
    icon: <IconAlert />,
    build: () => ({ priority: 'urgent', overdue: true, dueDateTo: daysAgoStr(2) }),
    activeCheck: (f) => f.priority === 'urgent' && f.overdue && f.dueDateTo === daysAgoStr(2),
  },
];

// ─── Preset chip ───────────────────────────────────────────────────────────────
const PresetChip: React.FC<{ label: string; icon: React.ReactNode; active: boolean; onClick: () => void }> = ({ label, icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all whitespace-nowrap ${
      active
        ? 'bg-accent-fill border-accent text-on-accent'
        : 'border-ui-border bg-ui-subtle text-ui-text2 hover:border-accent/25 hover:text-ui-text'
    }`}
  >
    <span className="flex-shrink-0">{icon}</span>
    {label}
    {active && (
      <span className="flex-shrink-0 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/20">
        <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </span>
    )}
  </button>
);

// ─── Component ─────────────────────────────────────────────────────────────────
interface TaskListProps {
  initialStatus?: string;
  initialPriority?: string;
  initialAssigneeUserId?: string;
  initialOwnerUserId?: string;
  initialUpdatedByUserId?: string;
  initialFollowerUserId?: string;
  initialSearch?: string;
  initialOverdue?: boolean;
  initialUnassigned?: boolean;
  /** Due-date bounds as ISO strings or 'YYYY-MM-DD'. Needed so a SAM-published filter like
   *  "tasks due on the 18th" reaches the list — without these the agent answered correctly
   *  while the panel underneath showed the unfiltered set. */
  initialDueDateFrom?: string;
  initialDueDateTo?: string;
  initialQueueRoleId?: string;
  initialContextType?: string;
  initialContextEntityId?: string;
  /** Comma-separated task ids — show ONLY these (SAM multi-create rule FILTER_CREATED). Clearable. */
  initialIds?: string;
  /** When false (SAM workspace mode): shows all tenant tasks instead of only the current user's. Defaults to true. */
  initialMine?: boolean;
  /** When true (SAM workspace mode): hides all controls. */
  hideControls?: boolean;
  /** Called when a card is clicked (workspace mode). Defaults to navigate('/tasks/:id'). */
  onSelectTask?: (taskId: string) => void;
  /** Rows SAM's `search_tasks` tool already fetched for this exact filter (workspace mode). Rendered
   *  immediately in place of a loading spinner while this component's own first fetch is still in
   *  flight, so the card never flashes empty while the tool's narrated count is already on screen.
   *  Once the live query resolves it takes over as the source of truth (this is a seed, not a
   *  substitute) — any user interaction (filter/search/page) always goes through the live query. */
  initialRecords?: TaskFull[];
  /** Task ID to auto-expand inline on mount (workspace get_task flow). */
  initialExpandedId?: string;
  initialSortBy?: string;
  initialSortDir?: number;
  initialAdvancedFilters?: TaskAdvancedFilters;
  /** When set (e.g. opened directly from a notification link), show ONLY this task — no
   *  filters, chips, search, or pagination — instead of the full list. */
  singleTaskId?: string;
  /** Called when the user leaves single-task mode to go back to browsing the full list. */
  onExitSingleTask?: () => void;
  /** Workspace mode: report a MANUALLY changed filter so it joins the "⋯" recent-views menu
   *  alongside the agent-driven views. See `useWorkspaceFilterReport`. */
  onFiltersChange?: OnWorkspaceFilterChange;
}

const TaskList: React.FC<TaskListProps> = ({
  initialStatus = 'open', initialPriority = '', initialAssigneeUserId = '', initialOwnerUserId = '',
  initialUpdatedByUserId = '', initialFollowerUserId = '', initialSearch = '', initialOverdue = false, initialUnassigned = false,
  initialDueDateFrom = '', initialDueDateTo = '', initialQueueRoleId = '',
  initialContextType = '', initialContextEntityId = '', initialIds = '',
  initialMine = true, hideControls = false, onSelectTask, initialRecords, initialExpandedId, initialSortBy, initialSortDir,
  initialAdvancedFilters,
  singleTaskId, onExitSingleTask: _onExitSingleTask, onFiltersChange,
}) => {
  const singleTaskResult = useGetTaskByIdQuery(singleTaskId as string, { ...WORKSPACE_QUERY_OPTIONS, skip: !singleTaskId });
  const currentUserId = useAppSelector((s) => s.sessionState.userId) ?? '';
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(singleTaskId ?? initialExpandedId ?? null);

  useEffect(() => {
    if (initialExpandedId !== undefined) setExpandedTaskId(initialExpandedId ?? null);
  }, [initialExpandedId]);

  useEffect(() => {
    if (singleTaskId) setExpandedTaskId(singleTaskId);
  }, [singleTaskId]);

  const [isQueueView, setIsQueueView] = useState(false);

  const [status, setStatus] = useState(initialStatus);
  const [priority, setPriority] = useState(initialPriority);
  const [taskType, setTaskType] = useState('');
  const [dueDateFrom, setDueDateFrom] = useState(initialDueDateFrom);
  const [dueDateTo, setDueDateTo] = useState(initialDueDateTo);
  const [overdue, setOverdue] = useState(initialOverdue);
  const [unassigned, setUnassigned] = useState(initialUnassigned);
  const [assigneeUserId, setAssigneeUserId] = useState(initialAssigneeUserId);
  const [ownerUserId, setOwnerUserId] = useState(initialOwnerUserId);
  const [updatedByUserId, setUpdatedByUserId] = useState(initialUpdatedByUserId);
  const [followerUserId, setFollowerUserId] = useState(initialFollowerUserId);
  // A SAM-published `search` is a FILTER and composes with the rest of the filter set; a search
  // the user types into the box is a standalone lookup that deliberately pauses the filters and
  // matches more broadly (description and property address too, via /tasks/search).
  //
  // Telling them apart matters: "completed tasks with ZZC in the title" had SAM answer "2" while
  // the panel underneath showed 6, because its `search` was being fed into the box and silently
  // dropped the `status=completed` it arrived with. Provenance decides the path, so neither
  // behaviour has to change to fix the contradiction.
  const [samSearch, setSamSearch] = useState((initialSearch ?? '').trim());
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [advanced, setAdvanced] = useState<TaskAdvancedFilters>(initialAdvancedFilters ?? {});
  const [sortBy] = useState(initialSortBy ?? '');
  const [sortDir] = useState(initialSortDir ?? -1);
  const [page, setPage] = useState(0);
  const [ids, setIds] = useState(initialIds);
  // Re-sync when SAM points the list at a new set of ids; a user "clear" stays cleared until then.
  useEffect(() => {
    setIds(initialIds); setPage(0);
    // One just-created record → show it open, like the detail view a single create used to land on.
    const only = initialIds ? initialIds.split(',').filter(Boolean) : [];
    if (only.length === 1) setExpandedTaskId(only[0]);
  }, [initialIds]);
  const idCount = ids ? ids.split(',').filter(Boolean).length : 0;
  const [showFilters, setShowFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const limit = 20;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput.trim()), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchInput]);

  useEffect(() => {
    if (searchExpanded) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [searchExpanded]);

  // status='open' is the default — don't count it as a user-applied filter. status='' means the
  // user explicitly picked "All" (there's no separate "unset" state) and DOES count — the old
  // `status && status !== 'open' ? status : ''` short-circuited on the falsy '' before the
  // !== 'open' check ever ran, so explicitly choosing "All" silently didn't count as a filter,
  // and "Clear filters" never appeared even though the result set differed from the default.
  const activeCount = [status !== 'open' ? (status || 'all') : '', priority, taskType, dueDateFrom, dueDateTo, overdue ? 'x' : '', unassigned ? 'x' : '', assigneeUserId, ownerUserId, updatedByUserId, followerUserId, samSearch, isQueueView ? 'x' : '', idCount ? 'x' : '', ...Object.values(advanced)].filter(Boolean).length;
  const isSearching = searchQuery.length > 0;

  // `overdue` is a real server-side filter now (due_date strictly < now) and is sent as-is.
  // It used to be approximated here as due_date_to = end of today, which counted a task due at
  // 5pm today as already overdue at 9am — and the SAM tool approximated it differently again,
  // so the chip and the chat answer disagreed on the same data. One definition, server-side.

  // The list's effective filter. Extracted so the SAME object feeds both the query and the
  // recent-views report below — the "⋯" menu entry then replays exactly what is on screen.
  const queryArgs = !isSearching ? {
    ...(isQueueView
      ? { my_queues: true, skip: page * limit, limit }
      : {
          mine: ownerUserId || updatedByUserId || followerUserId ? false : initialMine,
          status: status || undefined,
          priority: priority || undefined,
          task_type: taskType || undefined,
          assignee_user_id: assigneeUserId || undefined,
          owner_user_id: ownerUserId || undefined,
          updated_by_user_id: updatedByUserId || undefined,
          follower_user_id: followerUserId || undefined,
          search: samSearch || undefined,
          queue_role_id: initialQueueRoleId || undefined,
          context_type: initialContextType || undefined,
          context_entity_id: initialContextEntityId || undefined,
          ids: ids || undefined,
          due_date_from: dueBound(dueDateFrom, false),
          due_date_to: dueBound(dueDateTo, true),
          overdue: overdue || (advanced.not_overdue ? false : undefined),
          unassigned: unassigned || undefined,
          exclude_assignee_user_id: advanced.exclude_assignee_user_id || undefined,
          created_from: dueBound(advanced.created_from ?? '', false),
          created_to: dueBound(advanced.created_to ?? '', true),
          no_due_date: advanced.no_due_date || undefined,
          title_starts_with: advanced.title_starts_with || undefined,
          sort_by: sortBy || undefined,
          sort_dir: sortBy ? sortDir : undefined,
          skip: page * limit,
          limit,
        }),
  } : undefined;

  const listResult = useGetTasksListQuery(queryArgs, { ...WORKSPACE_QUERY_OPTIONS, skip: isSearching });

  // Report user-driven filter changes (chips, search, queue toggle) to the "⋯" recent-views menu.
  useWorkspaceFilterReport(
    isSearching ? { search: searchQuery } : (queryArgs ?? {}),
    onFiltersChange,
  );

  const searchResult = useSearchTasksQuery(searchQuery, { skip: !isSearching });

  // Seed placeholder: only covers the very first, still-in-flight fetch — the moment listResult
  // has real data (including an empty page), that takes over as the source of truth.
  const hasSeed = initialRecords !== undefined;
  const usingSeed = !isSearching && hasSeed && listResult.isLoading && !listResult.data;

  const tasks: TaskFull[] = isSearching
    ? (searchResult.data ?? [])
    : usingSeed ? initialRecords! : (listResult.data?.data ?? []);
  const total = isSearching ? tasks.length : usingSeed ? initialRecords!.length : (listResult.data?.total ?? 0);
  const isLoading = isSearching ? searchResult.isLoading : (listResult.isLoading && !usingSeed);
  const isError = isSearching ? searchResult.isError : listResult.isError;
  const totalPages = isSearching ? 1 : Math.ceil(total / limit);

  const clearAll = () => {
    setStatus('open'); setPriority(''); setTaskType('');
    setDueDateFrom(''); setDueDateTo(''); setOverdue(false); setUnassigned(false);
    setAssigneeUserId(''); setOwnerUserId(''); setUpdatedByUserId(''); setFollowerUserId('');
    setSamSearch('');
    setAdvanced({});
    setIds('');
    setPage(0);
  };

  const currentFilters: FilterState = { status, priority, taskType, dueDateFrom, dueDateTo, overdue, assigneeUserId, ownerUserId, updatedByUserId, followerUserId };

  const applyPreset = (preset: Preset) => {
    setIsQueueView(false);
    const isActive = preset.activeCheck(currentFilters, currentUserId);
    clearAll();
    if (isActive) return; // toggle off
    const vals = preset.build(currentUserId);
    if (vals.status !== undefined) setStatus(vals.status);
    if (vals.priority !== undefined) setPriority(vals.priority);
    if (vals.taskType !== undefined) setTaskType(vals.taskType);
    if (vals.dueDateFrom !== undefined) setDueDateFrom(vals.dueDateFrom);
    if (vals.dueDateTo !== undefined) setDueDateTo(vals.dueDateTo);
    if (vals.overdue !== undefined) setOverdue(vals.overdue);
    if (vals.assigneeUserId !== undefined) setAssigneeUserId(vals.assigneeUserId);
    if (vals.ownerUserId !== undefined) setOwnerUserId(vals.ownerUserId);
    if (vals.updatedByUserId !== undefined) setUpdatedByUserId(vals.updatedByUserId);
    if (vals.followerUserId !== undefined) setFollowerUserId(vals.followerUserId);
    setPage(0);
  };

  const visiblePresets = PRESETS.filter((p) => p.id !== 'my_tasks' || !!currentUserId);


  // ── Role Queue chip (appended after PRESETS row) ────────────────────────────
  const IconUsers = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );

  // Leads the chip row (the row scrolls sideways — a trailing chip could sit off-screen).
  const createdChip = (
    <CreatedIdsChip count={idCount} titleKey="workspaceCreated.showAllTasks" onClear={() => { setIds(''); setPage(0); }} />
  );

  // singleTaskId: render the task inside the normal layout (with filters visible), not a bare card

  return (
    <div className="flex flex-col min-h-full space-y-2">

      {hideControls && createdChip}

      {/* ── Controls ── */}
      {!hideControls && (
        <>
          {/* ── Top control row ── */}
          <div className="flex items-center gap-2">

            {/* Search — icon (collapsed) or full input (expanded) */}
            {searchExpanded ? (
              <div className="relative flex-1 min-w-0">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ui-text3 pointer-events-none"
                  width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search tasks…"
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); setPage(0); }}
                  className="w-full pl-8 pr-8 py-1.5 rounded-lg border border-ui-border bg-ui-subtle text-xs text-ui-text placeholder:text-ui-text3 focus:outline-none focus:border-ui-ring/40 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearchExpanded(false); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ui-text3 hover:text-ui-text transition-colors"
                  title="Close search"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ) : (
              <>
                {/* Search icon button */}
                <button
                  type="button"
                  onClick={() => setSearchExpanded(true)}
                  title="Search tasks"
                  className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg border transition-colors ${
                    searchInput
                      ? 'bg-accent/10 border-accent/30 text-accent-text'
                      : 'border-ui-border bg-ui-subtle text-ui-text2 hover:border-accent/25 hover:text-ui-text'
                  }`}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </button>

                {/* Preset chips fill remaining space */}
                <div className="flex-1 overflow-x-auto no-scrollbar min-w-0">
                  <div className="flex gap-1.5 min-w-max">
                    {createdChip}
                    {visiblePresets.map((preset) => (
                      <PresetChip
                        key={preset.id}
                        label={preset.label}
                        icon={preset.icon}
                        active={preset.activeCheck(currentFilters, currentUserId)}
                        onClick={() => applyPreset(preset)}
                      />
                    ))}
                    {/* Role Queue chip */}
                    <PresetChip
                      label="Role Queue"
                      icon={<IconUsers />}
                      active={isQueueView}
                      onClick={() => { setIsQueueView((v) => !v); if (isQueueView) clearAll(); }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Status indicator chip — always visible so the current status filter is unambiguous */}
            {!isQueueView && !isSearching && (
              <button
                type="button"
                onClick={() => setShowFilters(true)}
                title="Click to change status filter"
                className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                  status === 'completed'
                    ? 'border-success/30 bg-success/10 text-success-text'
                    : status === 'cancelled'
                    ? 'border-ui-border bg-ui-hover text-ui-text2'
                    : status === ''
                    ? 'border-ui-border bg-ui-subtle text-ui-text3 hover:border-accent/25'
                    : 'border-accent/30 bg-accent/10 text-accent-text'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  status === 'completed' ? 'bg-success' : status === 'cancelled' ? 'bg-steel' : status === '' ? 'bg-ui-text3' : 'bg-accent'
                }`} />
                {status === '' ? 'All' : status === 'open' ? 'Open' : status === 'completed' ? 'Done' : status === 'cancelled' ? 'Cancelled' : status}
              </button>
            )}

            {/* Filter button — always visible */}
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                showFilters || activeCount > 0
                  ? 'bg-accent/10 border-accent/30 text-accent-text'
                  : 'border-ui-border bg-ui-subtle text-ui-text2 hover:border-accent/25 hover:text-ui-text'
              }`}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              Filter
              {activeCount > 0 && (
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-accent-fill text-on-accent text-xs font-bold leading-none">
                  {activeCount}
                </span>
              )}
            </button>

          </div>

          {/* Clear filters link */}
          {activeCount > 0 && !showFilters && (
            <div className="flex items-center justify-end">
              <button type="button" onClick={clearAll} className="text-xs text-ui-text3 hover:text-accent-text transition-colors">
                Clear filters
              </button>
            </div>
          )}

          {isSearching && (
            <p className="text-xs text-ui-text3">
              Searching for <span className="font-medium text-ui-text">"{searchQuery}"</span>
              {' — '}filters are paused while searching.
            </p>
          )}
        </>
      )}

      {/* ── Chip row (hideControls / workspace mode) ── */}
      {hideControls && (
        <div className="overflow-x-auto no-scrollbar -mx-0.5 px-0.5">
          <div className="flex gap-1.5 min-w-max">
            {visiblePresets.map((preset) => (
              <PresetChip
                key={preset.id}
                label={preset.label}
                icon={preset.icon}
                active={preset.activeCheck(currentFilters, currentUserId)}
                onClick={() => { setIsQueueView(false); applyPreset(preset); }}
              />
            ))}
            <PresetChip
              label="Role Queue"
              icon={<IconUsers />}
              active={isQueueView}
              onClick={() => { setIsQueueView((v) => !v); if (isQueueView) clearAll(); }}
            />
          </div>
        </div>
      )}

      {/* ── Filter panel — inline below controls, above task list ── */}
      {showFilters && !hideControls && (
        <div className="rounded-xl border border-ui-border bg-ui-card shadow-sm px-3 pt-2.5 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-micro font-semibold text-ui-text2 uppercase tracking-wide">Filter</span>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <button type="button" onClick={clearAll} className="text-xs text-ui-text3 hover:text-accent-text transition-colors">
                  Clear all
                </button>
              )}
              <button type="button" onClick={() => setShowFilters(false)} className="text-ui-text3 hover:text-ui-text transition-colors p-0.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
          <TaskListFilters
            status={status}
            priority={priority}
            taskType={taskType}
            dueDateFrom={dueDateFrom}
            dueDateTo={dueDateTo}
            overdue={overdue}
            onStatusChange={(v) => { setStatus(v); setPage(0); }}
            onPriorityChange={(v) => { setPriority(v); setPage(0); }}
            onTaskTypeChange={(v) => { setTaskType(v); setPage(0); }}
            onDueDateFromChange={(v) => { setDueDateFrom(v); setPage(0); }}
            onDueDateToChange={(v) => { setDueDateTo(v); setPage(0); }}
            onOverdueChange={(v) => { setOverdue(v); setPage(0); }}
          />
        </div>
      )}

      {/* ── Task list (shared for normal, search, and role-queue views) ── */}
      <>
        {singleTaskId ? (
          <>
            {singleTaskResult.isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
            {singleTaskResult.isError && (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-ui-text3">
                  <rect x="3" y="3" width="18" height="18" rx="3"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="16" x2="12" y2="16"/>
                </svg>
                <p className="text-sm font-medium text-ui-text2">No tasks to show</p>
              </div>
            )}
            {singleTaskResult.data && (
              <div className="grid grid-cols-1 gap-3 pb-2">
                <TaskCard
                  task={singleTaskResult.data}
                  onSelect={onSelectTask}
                  expandedId={expandedTaskId}
                  onExpand={setExpandedTaskId}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {isLoading && <div className="flex justify-center py-12"><Spinner /></div>}
            {isError && (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <p className="text-sm font-medium text-ui-text2">No tasks to show</p>
              </div>
            )}
            {!isLoading && !isError && tasks.length === 0 && (
              <p className="text-center text-ui-text3 py-8">
                {isQueueView ? 'No tasks in your role queue.' : 'No tasks found.'}
              </p>
            )}

            <div className="grid grid-cols-1 gap-3 pb-2">
              {tasks.map((task) => (
                <TaskCard
                  key={task.task_id}
                  task={task}
                  onSelect={onSelectTask}
                  expandedId={expandedTaskId}
                  onExpand={setExpandedTaskId}
                />
              ))}
            </div>
          </>
        )}

        {!hideControls && !singleTaskId && (
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        )}
      </>

    </div>
  );
};

export default TaskList;
